import React from 'react';
import { connect } from 'react-redux';
import { Redirect, useHistory } from 'react-router-dom';
import { finishNestedFlow, submitData } from '../../actions/userData';
import { scrollTop } from '../../animation/scrollTo';
import { getMaritalStatus } from '../../calculations/userDataTransformer';
import { validateEmployment } from '../flows/validate/incomeValidator';
import { ConnectedFlow } from './ConnectedFlow';

function makeOtherIncomeQuestionsForUser(prefix, userName) {
    return [
        {
            questions: [
                { id: prefix + 'WorkedForGovernment', type: 'boolean', description: `Worked for the Government`, text: `Is ${userName} eligible for a government pension?` },
            ],
            text: `Other Income for ${userName}`,
            shouldShow: () => true,
            descriptor: 'Other Income'
        },
        {
            questions: [
                { id: prefix + 'PensionTable', type: 'incomeTable', description: `Pension`, text: 'Pension Information', tableType: 'single', buttonDescription: 'Add Government Pension Details +', questionSections: makePensionQuestions(true, userName) },
            ],
            shouldShow: (answers) => answers[prefix + 'WorkedForGovernment'] === true,
        },
        {
            questions: [{
                id: prefix + 'NonGovtPensionEligible',
                type: 'boolean',
                description: `Eligible for non-govt pension`,
                text: `Is ${userName} eligible for a pension from employment?`
            },
            ],
            shouldShow: () => true,
        },
        {
            questions: [{
                id: prefix + 'NonGovtPensionTable',
                type: 'incomeTable',
                description: `Pension`, text: 'Pension Information',
                tableType: 'single', buttonDescription: 'Add Pension Details +', questionSections: makePensionQuestions(false, userName)
            },
            ],
            shouldShow: (answers) => answers[prefix + 'NonGovtPensionEligible'] === true,
        },
        {
            questions: [
                {
                    id: prefix + 'Annuity', type: 'boolean',
                    description: `Annuity`, text: `Does ${userName} own an annuity that has a Lifetime Income benefit?`
                }
            ],
            shouldShow: () => true,
            text: 'Annuity / Future Income',
            descriptor: 'Other Income'
        },
        {
            questions: [
                {
                    id: prefix + 'AnnuityTable',
                    type: 'incomeTable',
                    description: `Annual Annuity Payment`,
                    text: 'Add Annuity Details', buttonDescription: 'Add Income Annuity +', questionSections: annuitySubQuestions(userName)
                },
            ],
            shouldShow: (answers) => answers[prefix + 'Annuity'] === true,
        },
        {
            questions: [
                {
                    id: prefix + 'Alimony',
                    type: 'dollar',
                    description: `Alimony`,
                    text: `How much does ${userName} receive from alimony annually?`, help: "We need to collect all of your current income"
                },
            ],
            shouldShow: (answers) => answers[prefix + 'WorkedForGovernment'] !== undefined,
        },
        {
            questions: [
                {
                    id: prefix + 'extraIncomeTable',
                    type: 'incomeTable',
                    description: `Additional Income`,
                    text: `Add additional sources of income for ${userName}`,
                    help: `Identify source and enter any income for ${userName} that we have not identified`,
                    questionSections: futureIncomeSubQuestions()
                },
            ],
            text: `Additional Income`,
            shouldShow: () => true,
        }
    ]
}

function makePensionQuestions(govt, userName) {
    let govtString = govt ? 'government ' : '';
    return [
        {
            questions: [
                { id: 'currentlyReceiving', type: 'boolean', description: `Currently Receive Pension`, text: `Does ${userName} currently receive a ${govtString}pension?`, help: "Government pensions can affect your Social Security benefits" },
            ],
            shouldShow: () => true,
        },
        {
            questions: [
                { id: 'amount', type: 'dollar', text: `How much does ${userName} receive annually?` },
            ],
            shouldShow: (answers) => answers.currentlyReceiving,
        },
        {
            questions: [
                { id: 'startDate', type: 'dob', description: `Start Date`, text: `When will ${userName} begin receiving this pension?` },
            ],
            shouldShow: (answers) => answers['currentlyReceiving'] === false,
        },
        {
            questions: [
                { id: 'amount', type: 'dollar', text: `How much will ${userName} receive annually?` },
            ],
            shouldShow: (answers) => answers.currentlyReceiving === false,
        },
        {
            questions: [
                { id: 'willEnd', type: 'wrongBoolean', description: `Pension Ends`, text: `Is this a lifetime benefit?`, help: "Will this continue for the rest of your life, or does it have an end date?" },
            ],
            shouldShow: (answers) => answers.currentlyReceiving !== undefined,
        },
        {
            questions: [
                { id: 'endDate', type: 'dob', description: `Pension End Date`, text: `When will ${userName}'s pension end?`, help: "Government pensions can affect your Social Security benefits" },
            ],
            shouldShow: (answers) => answers['willEnd'] === true,
        },
        {
            questions: [
                { id: 'isGPO', type: 'pension', text: `Did ${userName} pay social security taxes on the earnings from this job?`, }
            ],
            shouldShow: (answers) => govt ? true : false,
        },
        {
            questions: [
                { id: 'percentageIncrease', type: 'percentage', description: `Percentage Increase`, text: `How much will this benefit increase each year due to cost of living adjustments?` },
            ],
            shouldShow: (answers) => answers.currentlyReceiving !== undefined,
        },
    ]
}

function makeOtherIncomeQuestions(userName, spouseName, maritalStatus) {
    let questions = makeOtherIncomeQuestionsForUser('main', userName);

    if (maritalStatus === 'married') {
        questions = questions.concat(makeOtherIncomeQuestionsForUser('spouse', spouseName));
        questions.push({
            questions: [
                { id: 'rentalIncome', type: 'dollar', description: `Net Rental Income`, text: 'What is the net income the household receives from rental properties?', help: `Enter your NET income derived from your rental properties after all expenses and loan payments.` },
                { id: 'jointIncome', type: 'incomeTable', description: `Additional joint income`, text: `Add additional joint income here: `, questionSections: futureIncomeSubQuestions() },
            ],
            text: 'Joint Income',
            shouldShow: () => true,
        });
    } else {
        questions.push({
            questions: [
                { id: 'rentalIncome', type: 'number', description: `Net Rental Income`, text: 'What is the net income you received from rental properties?' },
            ],
            shouldShow: () => true,
        });
    }

    return questions;
}

function annuitySubQuestions(userName) {
    return [
        {
            questions: [
                { id: 'currentlyReceiving', type: 'boolean', description: `Currently Receiving Annuity`, text: `Is ${userName} currently receiving income from this annuity?` },
            ],
            shouldShow: () => true,
        },
        {
            questions: [
                { id: 'amount', type: 'dollar', description: `Amount`, text: `How much does ${userName} receive annually?` },
            ],
            shouldShow: (answers) => answers.currentlyReceiving === true,
        },
        {
            questions: [
                { id: 'startDate', type: 'dob', description: `Start Date`, text: `When will ${userName} begin to receive income from this annuity?` },
            ],
            shouldShow: (answers) => answers['currentlyReceiving'] === false,
        },
        {
            questions: [
                { id: 'amount', type: 'dollar', description: `Annual Annuity Payment`, text: `How much will ${userName} receive annually?` },
            ],
            shouldShow: (answers) => answers.currentlyReceiving === false,
        },
        {
            questions: [
                { id: 'percentageIncrease', type: 'percentage', description: `Benefit Increase`, text: `How much will this benefit increase each year due to cost of living adjustments?` },
            ],
            shouldShow: (answers) => answers.amount !== undefined,
        },
    ]
}

function futureIncomeSubQuestions() {
    return [
        {
            questions: [
                { id: 'name', type: 'string', text: `What is the name of this income source?`, help: 'i.e. pension, annuity, etc' },
                { id: 'amount', type: 'dollar', text: `How much income will this generate anually?`, help: 'Enter anticipated amount' },
                { id: 'currentlyReceiving', type: 'boolean', text: 'Have these payments begun yet?', help: 'Have you started receiving payments on this custom income source yet?' },
            ],
            shouldShow: () => true,
        },
        {
            questions: [
                { id: 'startDate', type: 'dob', text: 'When will payments begin?', help: 'Enter the date payments will begin' },
            ],
            shouldShow: (answers) => answers['currentlyReceiving'] === false
        },
        {
            questions: [
                { id: 'willEnd', type: 'boolean', text: 'Will these payments end?' },
            ],
            shouldShow: () => true
        },
        {
            questions: [
                { id: 'endDate', type: 'dob', text: 'When will payments end?' },
            ],
            shouldShow: (answers) => answers['willEnd']
        },
    ]
}

function makeDeductionsForUser(prefix, userName, answers) {
    return [
        {
            questions: [
                { id: prefix + 'PayrollDeductions', type: 'payrollDeductions', description: `Payroll Deductions`, text: '', help: "Illustrates your NET income to determine how much income is actually supporting your household expenses" },
            ],
            shouldShow: () => answers[prefix + 'IsEmployed'],
            text: 'Enter ' + userName + '\'s Payroll Deductions:',
            descriptor: userName + '\'s Payroll Deductions:'
        }
    ]
}

function makeEmploymentQuestions(prefix, userName) {
    return [
        {
            questions: [
                { id: prefix + 'IsEmployed', type: 'boolean', description: `${userName} Employment Details`, text: `Is ${userName} presently employed?` }
            ],
            shouldShow: () => true,
            text: 'Enter ' + userName + '\'s employment details:',
            descriptor: userName + '\'s Income:'
        },
        {
            questions: [
                { id: prefix + 'RetireDate', type: 'dob', description: `Retire Date`, text: `When will ${userName} retire?`, help: "Illustrates your Cash Flow after retirement. Enter your last date of employment" },
                { id: prefix + 'GrossIncome', type: 'dollar', description: `Gross Income`, text: `What is ${userName}'s annual gross income from employment (W2, K1, 1099...)?`, help: "We need to collect all of your current income" },
                { id: prefix + 'IncomePercentageIncrease', type: 'dollarOrPercent', description: `Income Increase`, text: `How much does ${userName}'s income increase each year?`, help: "Illustrates your future income while employed" },
                {
                    id: prefix + 'RetirementContributions',
                    type: 'dollarOrPercent',
                    description: `Retirement Contributions`,
                    text: `How much does ${userName} contribute annually to a retirement plan through work?`, help: "Illustrates new contributions to your Retirement Plan prior to retirement. (Contribution/Gross Income = %)"
                },
                { id: prefix + 'EmployerMatch', type: 'dollarOrPercent', description: `Retirement Contributions`, text: `How much does ${userName}'s employer contribute to a retirement plan each year?`, help: "Illustrates employer's matching contributions to your Retirement Plan prior to retirement." },
            ],
            shouldShow: (answers) => answers[prefix + 'IsEmployed'] === true,
        },
        {
            questions: [
                { id: prefix + 'IRAOrSEPContribution', type: 'dollar', description: `IRA / SEP Contributions`, text: `How much does ${userName} contribute to an IRA or SEP annually?`, help: "This is different from your employer's Retirement Plan (401K, 403B, etc). Do you contribute to an IRA or SEP?" },
                { id: prefix + 'RothIRAContribution', type: 'dollar', description: `Roth IRA`, text: `How much does ${userName} contribute to a Roth IRA annually?`, help: "This is different from your IRA/SEP. Do you contribute to a Roth IRA?" },
            ],
            shouldShow: () => true,
            text: 'Enter ' + userName + '\'s Other Contributions:',
            descriptor: userName + '\'s Other Contributions:'
        },
    ]
}

let makeFlows = (userName, spouseName, maritalStatus, answers) => ({
    employment: {
        questionData: maritalStatus === 'married' ? makeEmploymentQuestions('main', userName).concat(makeEmploymentQuestions('spouse', spouseName)) : makeEmploymentQuestions('main', userName),
        validator: (answers) => validateEmployment(answers, maritalStatus, userName, spouseName),
        next: '/app/income/deductions',
        previous: '/app/profile',
    },
    deductions: {
        questionData: maritalStatus === 'married' ? makeDeductionsForUser('main', userName, answers).concat(makeDeductionsForUser('spouse', spouseName, answers)) : makeDeductionsForUser('main', userName, answers),
        next: '/app/income/other',
        previous: '/app/income/employment',
    },
    other: {
        questionData: makeOtherIncomeQuestions(userName, spouseName, maritalStatus),
        next: '/app/expenses/housing',
        previous: '/app/income/deductions',
    },
});

const IncomeFlow = ({ userName, spouseName, maritalStatus, subTab, submitData, finishNestedFlow, answers }) => {
    const history = useHistory();
    let incomeFlows = makeFlows(userName, spouseName, maritalStatus, answers)
    if (incomeFlows[subTab]) {
        return <ConnectedFlow finishFlow={() => {
            scrollTop();
            history.push(incomeFlows[subTab].next);
            submitData();
            finishNestedFlow('income', incomeFlows, subTab);
        }} flowMode={true}
            userName={userName}
            spouseName={spouseName}
            questionSections={incomeFlows[subTab].questionData}
            sectionValidator={incomeFlows[subTab].validator}
            goBack={() => {
                scrollTop();
                history.push(incomeFlows[subTab].previous);
                submitData();
            }} maritalStatus={maritalStatus} />
    }

    return <Redirect to='/app/income/employment' />
}

export default connect(state => {
    return {
        userName: state.userData.answers.mainUserName,
        spouseName: state.userData.answers.spouseUserName,
        maritalStatus: getMaritalStatus(state.userData.answers),
        answers: state.userData.answers
    }
}, {
    finishNestedFlow,
    submitData
})(IncomeFlow);
